import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography, CircularProgress } from '@mui/material';
import useDraftStore from '../../../stores/useDraftStore';
import { useShallow } from 'zustand/shallow';

const SaveDraftDialog = () => {
  const [
    showSaveDialog,
    setShowSaveDialog,
    saveDialogData,
    setSaveDialogData,
    saveDraft,
    isSaving,
    error
  ] = useDraftStore(
    useShallow((state) => [
      state.showSaveDialog,
      state.setShowSaveDialog,
      state.saveDialogData,
      state.setSaveDialogData,
      state.saveDraft,
      state.isSaving,
      state.error
    ])
  );

  const [localName, setLocalName] = useState('');
  const [localDescription, setLocalDescription] = useState('');

  const handleClose = () => {
    setShowSaveDialog(false);
    setLocalName('');
    setLocalDescription('');
  };

  const handleSave = async () => {
    if (!localName.trim()) {
      return;
    }

    const result = await saveDraft(localName.trim(), localDescription.trim());

    if (result.success) {
      handleClose();
    }
  };

  const handleNameChange = (event) => {
    setLocalName(event.target.value);
  };

  const handleDescriptionChange = (event) => {
    setLocalDescription(event.target.value);
  };

  React.useEffect(() => {
    if (showSaveDialog) {
      // Reset form when dialog opens
      setLocalName('');
      setLocalDescription('');
    }
  }, [showSaveDialog]);

  return (
    <Dialog
      open={showSaveDialog}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Save Current State as Draft
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <TextField
            autoFocus
            label="Draft Name"
            fullWidth
            value={localName}
            onChange={handleNameChange}
            variant="outlined"
            placeholder="Enter a name for this draft"
            sx={{ mb: 2 }}
            disabled={isSaving}
          />

          <TextField
            label="Description (Optional)"
            fullWidth
            multiline
            rows={3}
            value={localDescription}
            onChange={handleDescriptionChange}
            variant="outlined"
            placeholder="Describe what's included in this draft"
            disabled={isSaving}
          />

          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              Error: {error}
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will save your current design state, including:
            • Tool settings and applied modifications
            • 3D model data
            • Undo history for seamless restoration
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!localName.trim() || isSaving}
          startIcon={isSaving ? <CircularProgress size={16} /> : null}
        >
          {isSaving ? 'Saving...' : 'Save Draft'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveDraftDialog;